import { Component, EventEmitter, OnDestroy, OnInit, Output, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { GtwElementsBaseUrlsModel } from '../../../shared/_models/gtw-elements-base-urls.model';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  extractObject,
  generalConfigFromGlobal,
  hydratedGlobalParams,
  normalizeFilterParams,
  rowPayloadFromActiveScreen
} from '../../../shared/workspace-element-context';
import { FirDiagnosticCenterService } from './fir-diagnostic-center.service';

@Component({
    selector: 'gtw-fir-diagnostic-center',
    templateUrl: './fir-diagnostic-center.component.html',
    styleUrls: ['./fir-diagnostic-center.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class FirDiagnosticCenterComponent implements OnInit, OnDestroy {

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  private readonly destroy$ = new Subject<void>();
  private isInitialized = false;
  private readonly contextSubs = new Subscription();
  private contextEpoch = 0;

  baseURLs: GtwElementsBaseUrlsModel = { admin: '', api: '', custom: '', efile: '', pdfEngine: '', bulkPdfEngine: '' };
  globalService: any = {};
  USER_SETTINGS: any = {};
  SERVER_CONFIG: any = {};
  GENERAL_CONFIG: any = {};
  filterParams: any = {};
  rowData: any = {};

  titleName = 'Module Inventory/Request Access';
  dashboardCards: any[] = [];
  dashboardCardsList: any[] = [];
  selectedChannel: any = null;
  gettingStatedPage = false;
  childDetailObj: any = null;
  loading = false;
  crudLoading = false;
  searchCard = { label: '' };

  constructor(
    private cdr: ChangeDetectorRef,
    private gtwSnackbarService: GtwSnackbarService,
    private diagnosticService: FirDiagnosticCenterService,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService
  ) {}

  ngOnInit(): void {
    this.resetAndTryInit();
    this.contextSubs.add(
      this.gtwWorkspace.setTabActive.subscribe(() => {
        this.resetAndTryInit();
      })
    );
    this.contextSubs.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => {
        this.resetAndTryInit();
      })
    );
  }

  ngOnDestroy(): void {
    this.contextEpoch++;
    this.contextSubs.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const shell = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    const hydrated = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || {};
    let fp = normalizeFilterParams(activeScreen);
    if (!Object.keys(fp).length) {
      fp = extractObject(hydrated);
    }
    this.filterParams = fp;
    const mergedGp = { ...extractObject(shell.globalParams), ...extractObject(hydrated) };
    this.globalService = {
      ...shell,
      globalParams: mergedGp,
      filterParams: fp,
      workspaceFactory: this.gtwWorkspace.workspaceFactory,
      getScenario: (ty: any) => this.gtwGlobal.getScenario(ty),
      getEIN: () => this.gtwGlobal.getEIN(),
      getTAXYEAR: () => this.gtwGlobal.getTAXYEAR(),
      getChartType: () => this.gtwGlobal.getChartType(),
      inputs: this.gtwGlobal.inputs,
      sso_id: this.gtwApp?.USER_SETTINGS?.user?.sso_id ?? ''
    };
    this.USER_SETTINGS = this.gtwApp.USER_SETTINGS || {};
    this.SERVER_CONFIG = this.gtwApp.SERVER_CONFIG || {};
    const gc = generalConfigFromGlobal(this.gtwGlobal);
    this.GENERAL_CONFIG = { ...gc, base_url: gc.base_url || gc.api || '' };
    this.baseURLs = gc;
    const rawRow = rowPayloadFromActiveScreen(activeScreen);
    this.rowData = Array.isArray(rawRow)
      ? extractObject(rawRow[0]?.data ?? rawRow[0])
      : extractObject(rawRow);
  }

  private resetAndTryInit(): void {
    this.contextEpoch++;
    this.isInitialized = false;
    this.applyWorkspaceContext();
    this.tryInit();
    this.cdr.markForCheck();
  }

  private tryInit() {
    if (this.isInitialized) return;
    if (!this.GENERAL_CONFIG || Object.keys(this.GENERAL_CONFIG).length === 0) return;
    this.isInitialized = true;
    this.init();
  }

  private init() {
    this.loadDashboardData();
  }

  private loadDashboardData() {
    this.loading = true;
    this.crudLoading = true;
    this.cdr.detectChanges();

    const params = {
      client_key: 0,
      user_id: this.USER_SETTINGS?.user?.sso_id || '',
      jurisdiction_key: 1
    };

    this.diagnosticService.getUserAssign(
      this.GENERAL_CONFIG.admin_api_url || this.baseURLs.admin,
      params
    ).pipe(takeUntil(this.destroy$)).subscribe(
      (data: any) => {
        if (data.callSuccess === '1') {
          this.dashboardCards = data.jsonObject || [];
          const dropdownOption = this.dashboardCards.find((item: any) => item.id === '5');
          if (dropdownOption) {
            this.channelChange(dropdownOption);
          }
        } else {
          this.gtwSnackbarService.openSnackBar(
            'An error has occurred. If this continues please contact support.',
            'gtw-snackbar--red'
          );
        }
        this.loading = false;
        this.crudLoading = false;
        this.cdr.detectChanges();
      },
      (error: any) => {
        console.error('Error loading dashboard data:', error);
        this.loading = false;
        this.crudLoading = false;
        this.cdr.detectChanges();
      }
    );
  }

  channelChange(channel: any) {
    this.selectedChannel = channel;
    this.dashboardCardsList = channel.children || [];
    this.cdr.detectChanges();
  }

  viewDetailsModal(children: any) {
    this.gettingStatedPage = true;
    this.childDetailObj = children;
    this.cdr.detectChanges();
  }

  closeDetails() {
    this.gettingStatedPage = false;
    this.cdr.detectChanges();
  }

  cancel() {
    this.requestGridUpdate.emit('cancel');
  }
}
